import type { ApiErrorBody } from '@halo/shared';
import { toDisplayText } from '@/domain/display-text';

const API_PREFIX = '/api/v1';

/** Lỗi do API trả về. `message` là tiếng Việt, hiện nguyên văn cho người dùng được. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, QueryValue>;
  /** Thân JSON. Muốn gửi file thô thì dùng `rawBody`. */
  body?: unknown;
  rawBody?: BodyInit;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/**
 * Địa chỉ đầy đủ của một điểm cuối. Chỉ cần tới khi TRÌNH DUYỆT tự gọi thay cho `apiRequest`: thẻ
 * `<audio src>` (tự tua bằng Range) và `<a download>` (tự hiện tiến độ tải, file không nằm trong RAM).
 */
export function apiUrl(path: string, query?: Record<string, QueryValue>): string {
  return buildUrl(path, query);
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${API_PREFIX}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `${url}?${text}` : url;
}

function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null; // thân không phải JSON (vd. trang lỗi của proxy) → coi như không có thân
  }
}

/** Nghe sự kiện «phiên hết hạn» ở MỘT chỗ (App) thay vì mỗi màn hình tự xử lý 401. */
type UnauthenticatedListener = () => void;
const unauthenticatedListeners = new Set<UnauthenticatedListener>();
export function onUnauthenticated(listener: UnauthenticatedListener): () => void {
  unauthenticatedListeners.add(listener);
  return () => unauthenticatedListeners.delete(listener);
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  let body: BodyInit | undefined = options.rawBody;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const method = options.method ?? (body === undefined ? 'GET' : 'POST');
  // Lớp «Bảo vệ» trước tên miền (06/10/2026, chủ dự án: «web không đăng nhập được», 503) thỉnh thoảng trả 503
  // «Service temporarily unavailable» dù máy chủ app vẫn khoẻ — câu trả lời ấy là chữ thường, không phải JSON
  // lỗi của app. Gặp thì chờ chút rồi gọi lại: đọc tới hai lần, lệnh ghi một lần (lớp ấy chưa chuyển yêu cầu
  // đi nên gọi lại không ghi đôi).
  const retries = method === 'GET' ? EDGE_RETRIES_READ : EDGE_RETRIES_WRITE;
  for (let attempt = 0; ; attempt += 1) {
    let response: Response;
    try {
      response = await fetch(buildUrl(path, options.query), {
        method,
        headers,
        body,
        credentials: 'same-origin',
        signal: options.signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new ApiError(0, 'NETWORK', 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.');
    }

    if (response.status === 204) return undefined as T;
    const payload = parseJson(await response.text());
    if (response.ok) return payload as T;
    if (isEdgeOutage(response.status, payload) && attempt < retries && !options.signal?.aborted) {
      await sleep(EDGE_RETRY_DELAY_MS * (attempt + 1));
      continue;
    }
    throw failureOf(path, response.status, payload);
  }
}

export const EDGE_RETRIES_READ = 2;
export const EDGE_RETRIES_WRITE = 1;
export const EDGE_RETRY_DELAY_MS = 600;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** 502/503/504 KHÔNG kèm JSON lỗi của app = lớp trung gian (Bảo vệ, Caddy) đang hụt, không phải API từ chối. */
export function isEdgeOutage(status: number, payload: unknown): boolean {
  const appError = Boolean(payload && typeof payload === 'object' && 'error' in payload);
  return (status === 502 || status === 503 || status === 504) && !appError;
}

/** Câu trả lời lỗi của máy chủ → `ApiError`; phiên hết hạn thì báo luôn cho App. */
function failureOf(path: string, status: number, payload: unknown): ApiError {
  const error = (payload as ApiErrorBody | null)?.error;
  const apiError = new ApiError(
    status,
    error?.code ?? 'UNKNOWN',
    error?.message ?? `Máy chủ trả lỗi ${status}`,
    error?.details,
  );
  if (apiError.isUnauthenticated && !path.startsWith('/auth/')) {
    unauthenticatedListeners.forEach((listener) => listener());
  }
  return apiError;
}

export interface UploadOptions {
  query?: Record<string, QueryValue>;
  /** Thân yêu cầu là BYTE THÔ của file này (không multipart). */
  file: Blob;
  /** Phần đã gửi đi của file, 0 → 1. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * Gửi MỘT file. Dùng `XMLHttpRequest` chứ không phải `fetch`: `fetch` không báo được tiến độ ĐƯA
 * LÊN, mà một file nhạc 200 MB trên mạng chậm mất vài phút — không có thanh tiến độ thì người dùng
 * tưởng app treo rồi bấm lại.
 */
/** Lỗi do CHÍNH MÌNH huỷ (bấm «Huỷ», rời trang) — không phải lỗi để báo đỏ. */
export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

export function uploadFile<T>(path: string, options: UploadOptions): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const aborted = () => new DOMException('Đã huỷ', 'AbortError');
    if (options.signal?.aborted) return reject(aborted());

    const request = new XMLHttpRequest();
    request.open('POST', buildUrl(path, options.query));
    request.setRequestHeader('Accept', 'application/json');
    // Nói rõ «byte thô»: không để trình duyệt tự đoán kiểu từ đuôi file (mỗi máy đoán một khác).
    request.setRequestHeader('Content-Type', 'application/octet-stream');
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0)
        options.onProgress?.(event.loaded / event.total);
    };
    request.onload = () => {
      const payload = parseJson(request.responseText);
      if (request.status >= 200 && request.status < 300) resolve(payload as T);
      else reject(failureOf(path, request.status, payload));
    };
    request.onerror = () =>
      reject(new ApiError(0, 'NETWORK', 'Mất kết nối giữa chừng. Kiểm tra mạng rồi thử lại.'));
    request.onabort = () => reject(aborted());
    options.signal?.addEventListener('abort', () => request.abort(), { once: true });
    request.send(options.file);
  });
}

export function errorMessage(error: unknown): string {
  // Lời của máy chủ giữ nguyên văn hệ thống cũ (có emoji, mũi tên) → đổi sang dạng font của app vẽ được.
  if (error instanceof ApiError) return toDisplayText(error.message);
  if (error instanceof Error) return error.message;
  return 'Đã xảy ra lỗi không xác định';
}
